import { LOADING, SET_USER_SESSION } from "../actions";
import { APP_MONITORING, POLL_ATHENA_QUERY } from "../../dynamo_db/actions";
import { Auth } from "aws-amplify";
import { getAnalyticsContext } from "../../../utils.js";

import { API, graphqlOperation } from "@aws-amplify/api";
import config from "../../../aws-exports.js";

let originalFetch = require("isomorphic-fetch");
let fetch = require("fetch-retry")(originalFetch);

API.configure(config);

export const restfulResources = {
  materials: "/materials",
  contracts: "/contracts",
  serialNumber: "/serial_number",
  swapMaterial: "/swap_material",
  order: "/order",
  orderDlApi: "/commondata/sap-order",
  bulkMaterials: "/materials/bulk",
  bulkContracts: "/contracts/bulk",
  placeholderMaterials: "/materials-placeholder",
  bulkPlaceholderMaterials: "/materials-placeholder/bulk",
  placeholderMaterial: "/material-placeholder",
  systems: "/systems",
  bulkSystems: "/systems/bulk",
  systemApplications: "/system_applications",
  bulkSystemApplications: "/system_applications/bulk",
  licenses: "/licenses",
  bulkLicenses: "/licenses/bulk",
  dataQualitySapOrders: "/data_quality_sap_order",
  hwsSnsTrail: "/hws_sns_trail",
  snSwap: "/materials/replace",
  materialSwap: "/materials/replace_entitlements",
  checkSnSwap: "/materials/check-replacement-validity",
  bulkReport: "/bulk_report",
  missingSnsStatus: "/missing_sns_status",
  getPresignedUrl: "/get_presigned_url",
  appMonitoring: "/app_monitoring",
  accessRequest: "/access_request",
  sfdcSynchronizerMonitoring: "/sfdc_data",
  sfdcSync: "/sfdc_sync",
  athenaMetadata: "/athena_metadata",
  athenaQuery: "/athena_query",
  athenaHistory: "/athena_history",
  clientError: "/client_error",
  myPortalQuery: "/my_portal_query",
  // dlapi-v2 (REACT_APP_DL_API_URL) — My Portal object upsert + analytics.
  myPortalObjects: "/my-portal/objects",
  myPortalAnalytics: "/my-portal/objects",
  unprocessedItems: "/unprocessed_items",
  unprocessedItem: "/unprocessed_item",
  unprocessedItemsResend: "/unprocessed_items_resend",
};

// Hard ceiling for any single API call. A request that never resolves (e.g. a
// corporate proxy that swallows the response after the CORS preflight) would
// otherwise leave the global loading modal spinning forever, because the redux
// action never reaches the reducer that flips `loading` back to false.
export const REQUEST_TIMEOUT_MS = 30000;

export async function getCognitoIdToken() {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.error("Error fetching auth token:", error);
    return null;
  }
}

async function performLogout() {
  console.error("Authentication failed - logging out user");
  localStorage.clear();
  await Auth.signOut();
  window.location.reload();
}

export function parseDynamoResult(data) {
  const parsedData = {};

  for (const key in data) {
    const valueObj = data[key];
    parsedData[key] = Object.values(valueObj)[0];
  }
  return parsedData;
}
const resourceSet = new Set([
  "order",
  "serialNumber",
  "dataQualitySapOrders",
  "hwsSnsTrail",
  "bulkReport",
  "swapMaterial",
  "missingSnsStatus",
  "getPresignedUrl",
  "appMonitoring",
  "accessRequest",
  "sfdcSynchronizerMonitoring",
  "sfdcSync",
  "athenaMetadata",
  "athenaQuery",
  "athenaHistory",
  "clientError",
  "myPortalQuery",
  "unprocessedItems",
  "unprocessedItem",
  "unprocessedItemsResend",
]);

// Resources served by the dlapi-v2 REST API (REACT_APP_DL_API_URL) rather than
// the se-engine backend. Everything not in resourceSet and not here falls
// through to REACT_APP_SE_URL.
const dlApiResourceSet = new Set([
  "orderDlApi",
  "myPortalObjects",
  "myPortalAnalytics",
]);

const CLIENT_ERROR_BUFFER_KEY = "victoria_client_errors";
const CLIENT_ERROR_BUFFER_MAX = 50;

// The report POST is the one thing that must survive the exact conditions that
// caused the failure (degraded network, hung Cognito refresh). So we persist
// every diagnostic to localStorage first, then try to flush the buffer. If the
// live send fails, the entry stays buffered and is retried after the next
// successful API call (see getFromServer). This guarantees delivery as long as
// any later request eventually succeeds.
function readClientErrorBuffer() {
  try {
    return JSON.parse(localStorage.getItem(CLIENT_ERROR_BUFFER_KEY) || "[]");
  } catch {
    return [];
  }
}

// Exposed so the UI can offer a manual download when the report cannot be
// delivered (e.g. the whole se-admin API is blocked on the user's network).
export function getBufferedClientErrors() {
  return readClientErrorBuffer();
}

function writeClientErrorBuffer(list) {
  try {
    localStorage.setItem(
      CLIENT_ERROR_BUFFER_KEY,
      JSON.stringify(list.slice(-CLIENT_ERROR_BUFFER_MAX)),
    );
  } catch {
    // localStorage unavailable/full — nothing more we can do.
  }
}

// Send any buffered diagnostics. `idToken` is reused from the caller so this
// path never triggers its own Cognito refresh (which can hang on the very
// network that failed). Entries that send successfully are removed; the rest
// stay for the next flush.
export async function flushBufferedClientErrors(idToken) {
  const buffer = readClientErrorBuffer();
  if (!buffer.length) {
    return;
  }
  const headers = { "Content-Type": "application/json" };
  if (idToken) {
    headers.Authorization = "Bearer " + idToken;
  }
  const remaining = [];
  for (const entry of buffer) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const res = await originalFetch(
        process.env.REACT_APP_SE_PROXY_URL + restfulResources.clientError,
        {
          method: "POST",
          signal: controller.signal,
          keepalive: true,
          headers,
          body: JSON.stringify(entry),
        },
      );
      clearTimeout(timeoutId);
      if (!res || !res.ok) {
        remaining.push(entry); // keep for retry on a non-2xx
      }
    } catch (e) {
      remaining.push(entry); // network/timeout — keep for retry
    }
  }
  writeClientErrorBuffer(remaining);
}

// Fire-and-forget: buffer a front-end blocking issue with as much diagnostic
// context as we can gather, raise an in-app banner, then attempt to flush. It
// never throws and never shows the loading spinner, so it is safe to call from
// inside a failing request. It must NEVER report on the client_error endpoint
// itself, otherwise a failing report would recurse.
export async function reportClientError(details) {
  try {
    if (details?.resource === "clientError") {
      return;
    }

    // Token is passed in from the failing request so we don't call
    // Auth.currentSession() again here (it can hang on a degraded network).
    const { idToken, ...rest } = details || {};

    const conn =
      (typeof navigator !== "undefined" && navigator.connection) || {};
    const analytics = getAnalyticsContext();
    const diagnostics = {
      request_user: analytics?.username || "anonymous",
      ...rest,
      page:
        typeof window !== "undefined"
          ? window.location.pathname + window.location.hash
          : undefined,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
      language:
        typeof navigator !== "undefined" ? navigator.language : undefined,
      connection: {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
      },
      proxyUrl: process.env.REACT_APP_SE_PROXY_URL,
      timestamp: new Date().toISOString(),
    };

    // 1) Persist first, so the diagnostic is never lost.
    writeClientErrorBuffer([...readClientErrorBuffer(), diagnostics]);

    // 2) Surface it on screen (no F12 needed).
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("victoria:client-error", { detail: diagnostics }),
      );
    }

    // 3) Try to send now; whatever fails stays buffered for the next flush.
    const token = idToken || (await getCognitoIdToken().catch(() => null));
    await flushBufferedClientErrors(token);
  } catch (e) {
    console.log("reportClientError failed (silent)", e);
  }
}

export async function getFromServer(questObject, method = "GET") {
  let restfulResource = questObject.resource;
  let params = questObject.params;
  let body = questObject.body;
  let responseCode = 0;

  const idToken = await getCognitoIdToken();
  if (!idToken) {
    await performLogout();
    throw new Error("Authentication failed - user logged out");
  }

  let _url;
  let _body;
  if (resourceSet.has(restfulResource)) {
    _url =
      process.env.REACT_APP_SE_PROXY_URL + restfulResources[restfulResource];
  } else if (dlApiResourceSet.has(restfulResource)) {
    _url = process.env.REACT_APP_DL_API_URL + restfulResources[restfulResource];
  } else {
    _url = process.env.REACT_APP_SE_URL + restfulResources[restfulResource];
  }

  // Analytics is a nested path (/my-portal/objects/{object_id}/analytics); the
  // object id is carried in params but must be interpolated, not sent as a query
  // string. Consume it here so it is dropped from the query-string loop below.
  if (restfulResource === "myPortalAnalytics" && params && params.object_id) {
    _url += "/" + encodeURIComponent(params.object_id) + "/analytics";
    params = { ...params };
    delete params.object_id;
  }

  if (params && params.id) {
    _url += "/" + params.id;
  } else if (params) {
    let keys = Object.keys(params);
    _url += "?";
    for (let i in keys) {
      let key = keys[i];
      let value = params[key];
      if (Array.isArray(value)) {
        for (let j in value) {
          _url +=
            "&" + encodeURIComponent(key) + "=" + encodeURIComponent(value[j]);
        }
      } else {
        _url += "&" + encodeURIComponent(key) + "=" + encodeURIComponent(value);
      }
    }
  }
  if (method === "POST" || method === "PUT") {
    _body = JSON.stringify(body);
  }

  const startTime = Date.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let errorType = null;

  try {
    let reply = await fetch(_url, {
      method: method,
      body: _body,
      signal: controller.signal,
      headers: {
        Authorization: "Bearer " + idToken,
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    })
      .then(function (response) {
        responseCode = response.status;
        console.log("middleware response", response);
        return response.json();
      })
      .then(function (json) {
        console.log("getFromServer then", json);
        return json;
      })
      .catch((err) => {
        // AbortError means our own timeout fired: the request hung with no
        // response (classic corporate-proxy / network-stall symptom).
        errorType = err && err.name === "AbortError" ? "timeout" : "network";
        responseCode = errorType === "timeout" ? 0 : 500;
        console.log("err", err);
        return { error: errorType, responseCode };
      });
    clearTimeout(timeoutId);

    if (responseCode === 401 || responseCode === 403) {
      await performLogout();
      throw new Error("Authentication failed - user logged out");
    }

    // A blocking front-end issue = the request never reached us (timeout/network)
    // or the server returned a hard error. Report it with full context so it is
    // visible in the admin Client Errors page and as an in-app banner. The
    // valid token from this request is handed to the reporter so it never has
    // to refresh Cognito on a degraded network.
    if (errorType || responseCode === 0 || responseCode >= 500) {
      reportClientError({
        resource: restfulResource,
        method,
        url: _url,
        params: params || null,
        errorType: errorType || "http",
        responseCode,
        durationMs: Date.now() - startTime,
        timedOutAfterMs: errorType === "timeout" ? REQUEST_TIMEOUT_MS : null,
        idToken,
      });
    } else if (restfulResource !== "clientError") {
      // Piggyback delivery of any previously-buffered reports on a healthy
      // request, so entries that couldn't be sent at failure time still arrive.
      flushBufferedClientErrors(idToken);
    }

    reply.responseCode = responseCode;
    return reply;
  } catch (err) {
    clearTimeout(timeoutId);
    console.error("getFromServer error:", err);
    throw err;
  }
}

export async function getFromGraphQlApi(query, body) {
  try {
    const args = {};
    for (const [key, value] of Object.entries(body)) {
      args[key] = value;
    }
    const response = await API.graphql(
      graphqlOperation(query, args, process.env.REACT_APP_GRAPHQL_KEY),
    );
    return response;
  } catch (err) {
    console.log(err);
  }
}

export function middleware({ dispatch }) {
  return function (next) {
    return async function (action) {
      switch (action.type) {
        case LOADING:
        case SET_USER_SESSION:
        case APP_MONITORING:
        case POLL_ATHENA_QUERY:
          break;
        default:
          dispatch({ type: LOADING, loading: true });
          break;
      }
      return next(action);
    };
  };
}
